/**
 * BillingHub — rebuilt billing hub body for the in-app-checkout feature.
 *
 * in-app-checkout Phase 3 Sessions 3.4 + 3.5 + 3.6 + Phase 3 FIX C.
 *
 * Replaces `<BillingTab>` as the body of the Settings → Billing pane.
 * The hub adopts the Membership aesthetic from the prototype fidelity spec
 * (`refs/prototype-fidelity-spec.md` → Screen A — Billing hub):
 *
 *   1. Subscription panel (ACTIVE/CANCELING badge, Adjust-plan right slot,
 *      Membership eyebrow + serif tier name + qualitative tagline + renew/cancel
 *      date, inline usage meter PERCENTAGE ONLY, Cancel/Resume affordance).
 *   2. Payment method panel (card brand chip + •••• last4 + exp — rendered only
 *      when `stripe_customer_id` is present; no-customer case gets the
 *      founding-partner note).
 *   3. Invoices panel — Date / Total (OPTION A: dollar amount inline) / Status /
 *      View link.  Dollar totals are transaction records, not usage meters, so
 *      the %-only copy rule does NOT apply (see fidelity-spec "Copy rule held").
 *
 * View machines:
 *   - showBespoke: when true the hub body is replaced by `<BespokeAwaiting>`.
 *   - showPlanChange: when true the hub body is replaced by `<PlanChange>`.
 *   Both `onDone`/`onSuccess` callbacks return to the normal hub view.
 *
 * Plan management (FIX C — portal retired):
 *   "Adjust plan" now opens the in-app `<PlanChange>` view (sets showPlanChange).
 *   The Stripe Customer Portal dependency (`usePortalSession`) is removed.
 *   Cancel / Resume continue to use `useCancelSubscription` / `useResumeSubscription`.
 *
 * 4-state coverage (ALL required per CLAUDE.md checklist):
 *   - LOADING  — usage row fetching; Skeleton panels.
 *   - ERROR    — isError on billing-usage; EmptyState + Retry.
 *   - EMPTY    — no `usage_balance` row (never-subscribed user); EmptyState +
 *                "Choose a membership" CTA → /billing/checkout.
 *   - SUCCESS  — full hub rendering.
 *
 * Copy rules:
 *   - Usage meter: PERCENTAGE ONLY — no `$`, no "Anthropic", no COGS math.
 *   - Invoice amounts: inline (Option A) — the REAL Stripe total varies under
 *     `always_invoice` proration; amount_paid is a transaction record, not an
 *     allowance meter.
 *   - Tier prices: shown only on checkout/pricing surfaces (not here).
 *
 * Token discipline:
 *   - No raw hex in this file.
 *   - Usage meter fill: `bg-danger` (≥95%) / `bg-warn` (75–94%) / `bg-accent`
 *     (<75%) — three complete literal classes (no interpolation, Tailwind v4
 *     no-safelist).
 *
 * Admin guard — defense-in-depth: `<BillingHub>` returns null for admin users
 * (same as BillingTab). The upstream tab strip and BillingUsageBar also
 * self-gate.
 */

import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { ExternalLink, Mailbox, RefreshCw } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { useAuth } from '@/hooks/use-auth';
import { useBillingUsage, asBillingUsageRow } from '@/hooks/use-billing-usage';
import { useUserProfile } from '@/hooks/use-user-profile';
import { useInvoices } from '@/hooks/use-invoices';
import { useSavedCard } from '@/hooks/use-saved-card';
import {
  useCancelSubscription,
  useResumeSubscription,
} from '@/hooks/use-cancel-resume';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { posthog } from '@/lib/posthog';
import { UpdateCard } from './UpdateCard';
import { BespokeAwaiting } from './BespokeAwaiting';
import { PlanChange } from './PlanChange';
import type { PlanChangeTierInfo } from './PlanChange';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** US dollar formatting — `$499` (no cents on whole-dollar amounts, 2 dp otherwise). */
function formatAmount(cents: number): string {
  const usd = cents / 100;
  return Number.isInteger(usd) ? `$${usd}` : `$${usd.toFixed(2)}`;
}

/** Short human date — `Jun 5, 2026`. */
function formatDate(iso: string | number): string {
  const d = typeof iso === 'number' ? new Date(iso) : new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Tiers API shape (mirrors checkout.tsx TierResponseRow — local to this file)
// ---------------------------------------------------------------------------

interface TierResponseRow {
  id: string;
  display_name: string;
  tagline: string;
  monthly_price_usd: number | null;
  sort_order?: number;
  contact_only?: boolean;
  is_active: boolean;
}

interface TiersEnvelope {
  data: TierResponseRow[];
}

// ---------------------------------------------------------------------------
// MembershipEyebrow (inline — same token spec as CheckoutTierCard)
// ---------------------------------------------------------------------------

function MembershipEyebrow() {
  return (
    <div className="font-mono text-[11px] font-medium uppercase tracking-[0.22em] text-accent">
      Membership
    </div>
  );
}

// ---------------------------------------------------------------------------
// UsageMeter (inline, PERCENTAGE ONLY)
// ---------------------------------------------------------------------------

interface UsageMeterProps {
  pct: number; // 0..1
  periodStart: string;
  periodEnd: string;
}

function UsageMeter({ pct, periodStart, periodEnd }: UsageMeterProps) {
  const displayPct = Math.round(pct * 100);

  // Three complete literal classes — Tailwind v4 no-safelist; no interpolation.
  const fillClass =
    displayPct >= 95 ? 'bg-danger' : displayPct >= 75 ? 'bg-warn' : 'bg-accent';

  const fillTextClass =
    displayPct >= 95
      ? 'text-danger'
      : displayPct >= 75
      ? 'text-warn'
      : 'text-accent';

  return (
    <div
      data-testid="billing-hub-usage-meter"
      className="rounded-[3px] border border-panel-border bg-input-bg px-3.5 py-3"
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[8px] font-semibold uppercase tracking-[1.4px] text-text-muted">
          Usage this period
        </span>
        <span
          className={clsx(
            'font-mono text-xs font-semibold tabular-nums',
            fillTextClass
          )}
          data-testid="billing-hub-usage-pct"
        >
          {displayPct}%
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-panel-border-hi">
        <div
          className={clsx('h-full rounded-full', fillClass)}
          style={{ width: `${Math.min(100, displayPct)}%` }}
          data-testid="billing-hub-usage-bar"
        />
      </div>
      <span className="mt-1.5 block font-mono text-[9px] tracking-[0.4px] text-text-dim">
        {formatDate(periodStart)} – {formatDate(periodEnd)} · {displayPct}% of
        your monthly allowance used
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function BillingHub() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { data, isLoading: usageLoading, isError, refetch } = useBillingUsage();
  const { invoices, isLoading: invoicesLoading } = useInvoices();

  // Bespoke-awaiting view machine: when true the hub body is replaced by
  // <BespokeAwaiting>. onDone returns to the normal hub view.
  const [showBespoke, setShowBespoke] = useState(false);

  // Plan-change view machine (FIX C): when true the hub body is replaced by
  // <PlanChange>. "Adjust plan" sets this; onDone/onBack returns to the hub.
  const [showPlanChange, setShowPlanChange] = useState(false);

  // User profile — provides bespoke_requested_at for the persistent
  // awaiting-banner (FIX 1). Called unconditionally (hooks rule); gated by
  // the admin guard and early-return branches below.
  const { profile } = useUserProfile();

  // Saved-card hook — enabled only for Stripe customers (hasStripeCustomer
  // is derived below, but we must call hooks unconditionally; the hook itself
  // gates on `hasStripeCustomer` via the `enabled` flag).
  const row = asBillingUsageRow(data);
  const hasStripeCustomer = !!row?.stripe_customer_id;
  const { savedCard, isLoading: savedCardLoading } = useSavedCard(
    user?.id,
    hasStripeCustomer
  );

  // In-app cancel / resume mutations — at_period_end policy, no redirect.
  const cancelSub = useCancelSubscription(user?.id);
  const resumeSub = useResumeSubscription(user?.id);

  // Tiers query — needed by <PlanChange> to build the radio cards.
  // Fetched unconditionally (hook must not be conditional) but only used when
  // showPlanChange is true. staleTime 60s mirrors checkout.tsx.
  const tiersQuery = useQuery<TierResponseRow[]>({
    queryKey: queryKeys.billing.tiers(),
    queryFn: async () => {
      const envelope = await api.get<TiersEnvelope>('/api/billing/tiers');
      return envelope.data;
    },
    staleTime: 60_000,
  });

  // PostHog: subscription_tier_viewed — fired exactly once per real mount.
  // Mirrors BillingTab's StrictMode ref-dedupe pattern.
  const viewFiredRef = useRef(false);
  useEffect(() => {
    if (viewFiredRef.current) return;
    viewFiredRef.current = true;
    posthog.capture('subscription_tier_viewed', {});
  }, []);

  // Admin guard — defense-in-depth.
  if (isAdmin) return null;

  // ── Bespoke-awaiting view machine ────────────────────────────────────────
  // When showBespoke is true, replace the hub body with the BespokeAwaiting
  // confirmation panel. onDone returns to the normal hub view.
  if (showBespoke) {
    return (
      <BespokeAwaiting userId={user?.id} onDone={() => setShowBespoke(false)} />
    );
  }

  // ── Plan-change view machine (FIX C) ──────────────────────────────────────
  // When showPlanChange is true, replace the hub body with the in-app
  // PlanChange flow. Requires: current tier id from the usage row, the two
  // paid (non-contact_only) tiers from the tiers API, and the user's period_end.
  // Only mounted when we have the usage row (Success state) AND tiers loaded.
  if (showPlanChange && row && tiersQuery.data) {
    const paidTiers: PlanChangeTierInfo[] = tiersQuery.data
      .filter((t) => t.is_active && !t.contact_only)
      .map((t) => ({
        id: t.id,
        displayName: t.display_name,
        tagline: t.tagline,
        monthlyPriceUsd: t.monthly_price_usd,
        sortOrder: t.sort_order ?? 0,
      }));

    // PlanChange expects exactly 2 tiers; if we have fewer or more, fall back
    // to the hub view gracefully (edge case: misconfigured catalog).
    if (paidTiers.length >= 2) {
      const sortedPaidTiers = paidTiers.sort(
        (a, b) => a.sortOrder - b.sortOrder
      );
      // Pick the OTHER paid tier as the initial target (upgrade/downgrade default).
      const otherTier =
        sortedPaidTiers.find((t) => t.id !== row.tier_id) ?? sortedPaidTiers[1];

      return (
        <PlanChange
          currentTierId={row.tier_id}
          paidTiers={[sortedPaidTiers[0], sortedPaidTiers[1]]}
          initialTargetTierId={otherTier.id}
          periodEnd={row.period_end}
          userId={user?.id}
          onSuccess={() => setShowPlanChange(false)}
          onBack={() => setShowPlanChange(false)}
        />
      );
    }
    // Fewer than 2 paid tiers — fall through to hub body.
    setShowPlanChange(false);
  }

  // --- Loading ---
  if (usageLoading) {
    return (
      <div data-testid="billing-hub-loading" className="flex flex-col gap-3">
        <Panel title="Subscription">
          <Skeleton className="h-28" />
        </Panel>
        <Panel title="Invoices">
          <Skeleton className="h-16" />
        </Panel>
      </div>
    );
  }

  // --- Error ---
  if (isError) {
    return (
      <div data-testid="billing-hub-error" className="flex flex-col gap-3">
        <Panel title="Subscription">
          <EmptyState
            icon={<RefreshCw size={32} />}
            title="Couldn't load your subscription"
            description="Something went wrong fetching your billing details. Try again in a moment."
            action={
              <Button
                variant="secondary"
                onClick={() => void refetch()}
                data-testid="billing-hub-retry"
              >
                <RefreshCw size={14} className="mr-1.5" />
                Retry
              </Button>
            }
          />
        </Panel>
      </div>
    );
  }

  // --- Empty (no subscription) ---
  if (!row) {
    return (
      <div data-testid="billing-hub-empty" className="flex flex-col gap-3">
        <Panel title="Subscription">
          <EmptyState
            icon={<RefreshCw size={32} />}
            title="No subscription"
            description="Choose a membership to start underwriting deals with Arcanum."
            action={
              <Button
                variant="primary"
                onClick={() => navigate({ to: '/billing/checkout' })}
                data-testid="billing-hub-choose-plan"
              >
                Choose a membership
              </Button>
            }
          />
        </Panel>
      </div>
    );
  }

  // --- Success ---
  // `row` and `hasStripeCustomer` are already derived above (before early-returns)
  // so hooks could be called unconditionally. Re-assert row is non-null here for
  // TypeScript (early-return above guarantees it for this branch).
  if (!row) return null;
  const cancelAtDate = row.cancel_at ? formatDate(row.cancel_at) : null;
  const renewDate = formatDate(row.period_end);
  const cancelScheduled = !!cancelAtDate;

  // bespoke_requested_at — non-null ⇒ render the persistent awaiting banner.
  // Driven by server-truth (useUserProfile); survives reload.
  const bespokeRequestedAt = profile?.bespoke_requested_at ?? null;

  return (
    <div data-testid="billing-hub-success" className="flex flex-col gap-3">
      {/* Awaiting-Bespoke banner — persistent, driven by server-truth
          `bespoke_requested_at` (research §13 / FIX 1). Renders above the
          cancel banner so it's the first thing a returning user sees. Blue/info
          tone: a pending request, not an error. Survives page reload. */}
      {bespokeRequestedAt && (
        <div
          role="status"
          data-testid="billing-hub-bespoke-awaiting-banner"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[2px] border border-blue/20 bg-blue-dim px-4 py-2.5 font-mono text-xs text-blue"
        >
          <Mailbox size={13} className="shrink-0" aria-hidden />
          <span className="font-semibold uppercase tracking-[1px]">
            Bespoke request received
          </span>
          <span aria-hidden>·</span>
          <span className="text-text-muted">
            Our team will be in touch. Requested{' '}
            {formatDate(bespokeRequestedAt)}.
          </span>
        </div>
      )}

      {/* Scheduled-to-cancel banner (at_period_end — mirrors BillingTab copy) */}
      {cancelScheduled && (
        <div
          role="status"
          aria-live="polite"
          data-testid="billing-hub-cancel-banner"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[2px] border border-warn/15 bg-warn-dim px-4 py-2.5 font-mono text-xs text-warn"
        >
          <span className="font-semibold uppercase tracking-[1px]">
            Subscription scheduled to cancel
          </span>
          <span aria-hidden>·</span>
          <span className="text-text-muted">
            Access ends on {cancelAtDate}. Manage subscription to resume.
          </span>
        </div>
      )}

      {/* Subscription panel */}
      <Panel
        title="Subscription"
        badge={cancelScheduled ? 'CANCELING' : 'ACTIVE'}
        right={
          hasStripeCustomer ? (
            <Button
              variant="primary"
              onClick={() => setShowPlanChange(true)}
              data-testid="billing-hub-adjust-plan"
            >
              Adjust plan
            </Button>
          ) : undefined
        }
      >
        <div className="flex flex-col gap-3">
          {/* Tier identity — Membership eyebrow + serif name + tagline + renew date */}
          <div className="flex flex-col gap-1">
            <MembershipEyebrow />
            <span
              className="font-serif text-[22px] font-semibold leading-tight tracking-[-0.01em] text-text-bright"
              data-testid="billing-hub-tier-name"
            >
              {row.tier_display_name}
            </span>
            {/* Qualitative allowance line — NO $, NO Anthropic, NO COGS math */}
            <span className="font-body text-xs text-text-muted">
              {row.usage_limiting_enabled
                ? `${row.tier_display_name} membership · monthly`
                : 'Capture mode — no monthly cap'}
            </span>
            <span
              className="mt-0.5 font-mono text-[10px] tracking-[0.4px] text-text-dim"
              data-testid="billing-hub-renew-date"
            >
              {cancelScheduled
                ? `Access ends on ${cancelAtDate}`
                : `Renews on ${renewDate}`}
            </span>
          </div>

          {/* Usage meter — PERCENTAGE ONLY */}
          <UsageMeter
            pct={row.pct}
            periodStart={row.period_start}
            periodEnd={row.period_end}
          />

          {/* Plan-management affordances — in-app Cancel / Resume (at_period_end)
              + Request Bespoke (Session 3.6) */}
          {hasStripeCustomer && (
            <div className="flex items-center gap-2 border-t border-panel-border pt-3">
              {cancelScheduled ? (
                <Button
                  variant="primary"
                  onClick={() => void resumeSub.mutateAsync()}
                  disabled={resumeSub.isPending}
                  data-testid="billing-hub-resume"
                >
                  {resumeSub.isPending ? 'Resuming…' : 'Resume subscription'}
                </Button>
              ) : (
                <Button
                  variant="danger"
                  onClick={() => void cancelSub.mutateAsync()}
                  disabled={cancelSub.isPending}
                  data-testid="billing-hub-cancel"
                >
                  {cancelSub.isPending ? 'Canceling…' : 'Cancel subscription'}
                </Button>
              )}
              {/* Bespoke entry point — always visible for Stripe customers;
                  clicking transitions the hub to <BespokeAwaiting>. */}
              <Button
                variant="ghost"
                onClick={() => setShowBespoke(true)}
                data-testid="billing-hub-request-bespoke"
              >
                Request Bespoke
              </Button>
            </div>
          )}

          {/* Founding-partner / admin-granted note */}
          {!hasStripeCustomer && (
            <div
              className="mt-1 border-t border-panel-border pt-3 font-mono text-xs text-text-muted"
              data-testid="billing-hub-admin-granted-note"
            >
              Founding Partner allowance granted by Arcanum admin · no
              self-serve subscription to manage.
            </div>
          )}
        </div>
      </Panel>

      {/* Payment method panel — only for Stripe customers.
          The panel is intentionally absent for no-customer (founding partner) accounts.
          UpdateCard uses the real SetupIntent flow (FIX B). */}
      {hasStripeCustomer && (
        <Panel title="Payment method">
          <div data-testid="billing-hub-payment-method">
            <UpdateCard
              savedCard={savedCard}
              isLoading={savedCardLoading}
              userId={user?.id}
            />
          </div>
        </Panel>
      )}

      {/* Invoices panel */}
      <Panel title="Invoices">
        <div className="flex flex-col" data-testid="billing-hub-invoices">
          {invoicesLoading && <Skeleton className="h-16" />}

          {!invoicesLoading && invoices.length === 0 && (
            <span
              data-testid="billing-hub-no-invoices"
              className="font-mono text-sm text-text-dim"
            >
              No invoices yet.
            </span>
          )}

          {!invoicesLoading && invoices.length > 0 && (
            <>
              {/* Table header */}
              <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 border-b border-panel-border pb-2">
                {['Date', 'Total', 'Status', ''].map((h, i) => (
                  <span
                    key={i}
                    className={clsx(
                      'font-mono text-[8px] font-semibold uppercase tracking-[1.4px] text-text-muted',
                      i === 1 && 'text-right'
                    )}
                  >
                    {h}
                  </span>
                ))}
              </div>

              {/* Invoice rows */}
              {invoices.map((inv) => (
                <div
                  key={inv.id}
                  data-testid={`billing-hub-invoice-${inv.id}`}
                  className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 border-b border-panel-border/50 py-2.5 last:border-b-0"
                >
                  {/* Date + invoice number */}
                  <span className="flex flex-col min-w-0">
                    <span className="font-mono text-xs text-text-bright truncate">
                      {inv.number ?? `Invoice ${inv.id.slice(0, 8)}`}
                    </span>
                    <span className="font-body text-[10px] text-text-muted">
                      {formatDate(inv.created * 1000)}
                    </span>
                  </span>

                  {/* Total — OPTION A: dollar amount (transaction record, not usage meter) */}
                  <span
                    className="text-right font-mono text-xs tabular-nums text-text"
                    data-testid={`billing-hub-invoice-${inv.id}-total`}
                  >
                    {formatAmount(inv.amount_paid)}
                  </span>

                  {/* Status */}
                  <span
                    className={clsx(
                      'font-mono text-[9px] uppercase tracking-[0.4px]',
                      inv.status === 'paid'
                        ? 'text-success'
                        : inv.status === 'open'
                        ? 'text-warn'
                        : 'text-text-muted'
                    )}
                    data-testid={`billing-hub-invoice-${inv.id}-status`}
                  >
                    {inv.status}
                  </span>

                  {/* View link */}
                  {inv.hosted_invoice_url ? (
                    <a
                      href={inv.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`billing-hub-invoice-${inv.id}-view`}
                      className="inline-flex items-center gap-1 font-mono text-[10px] text-accent hover:underline shrink-0"
                    >
                      View <ExternalLink size={10} />
                    </a>
                  ) : (
                    <span />
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}
